import { RuntimeVersion } from "../types";

export function mapSkeletonVersionToRuntime(version: string | null): RuntimeVersion {
  if (!version) {
    return "4.1";
  }
  const match = version.match(/(\d+)\.(\d+)/);
  if (!match) {
    return "4.1";
  }
  const major = parseInt(match[1], 10);
  const minor = parseInt(match[2], 10);
  if (major < 4) {
    return "3.8";
  }
  if (major === 4 && minor <= 0) {
    return "4.0";
  }
  if (major === 4 && minor === 1) {
    return "4.1";
  }
  if (major === 4 && minor >= 2) {
    return "4.2";
  }
  return "4.1";
}

export function getRuntimeScriptUrl(runtimeVersion: RuntimeVersion): string {
  if (runtimeVersion === "3.8") {
    // 3.8 was not published to npm under @esotericsoftware/spine-player, so we load from github via jsdelivr
    return "https://cdn.jsdelivr.net/gh/EsotericSoftware/spine-runtimes@3.8/spine-ts/build/spine-player.js";
  }
  if (runtimeVersion === "4.0") {
    return "https://unpkg.com/@esotericsoftware/spine-player@4.0.28/dist/iife/spine-player.min.js";
  }
  if (runtimeVersion === "4.1") {
    return "https://unpkg.com/@esotericsoftware/spine-player@4.1.56/dist/iife/spine-player.min.js";
  }
  return "https://unpkg.com/@esotericsoftware/spine-player@4.2.109/dist/iife/spine-player.min.js";
}

export function getRuntimeCssUrl(runtimeVersion: RuntimeVersion): string {
  if (runtimeVersion === "3.8") {
    // Fallback to 4.1 CSS since 3.8 CSS is not available on CDN and 4.1 is compatible enough (as used previously)
    return "https://unpkg.com/@esotericsoftware/spine-player@4.1.56/dist/spine-player.css";
  }
  if (runtimeVersion === "4.0") {
    return "https://unpkg.com/@esotericsoftware/spine-player@4.0.28/dist/spine-player.css";
  }
  if (runtimeVersion === "4.1") {
    return "https://unpkg.com/@esotericsoftware/spine-player@4.1.56/dist/spine-player.css";
  }
  return "https://unpkg.com/@esotericsoftware/spine-player@4.2.109/dist/spine-player.css";
}

export function extractVersionFromBinary(buffer: ArrayBuffer): string | null {
  try {
    const bytes = new Uint8Array(buffer);
    const maxLen = Math.min(bytes.length, 100); // Only check first 100 bytes is usually enough
    let headerText = "";
    // Improved ASCII extraction: be stricter about valid version chars
    for (let i = 0; i < maxLen; i++) {
      const b = bytes[i];
      if (b >= 32 && b <= 126) {
        headerText += String.fromCharCode(b);
      } else {
        headerText += " "; // Treat non-printable as separator
      }
    }
    
    const match = headerText.match(/\b(3|4)\.(\d+)(?:\.(\d+))?/);
    if (match) {
      const fullVersion = match[0];
      console.log(`Extracted version from binary: ${fullVersion} (Raw header: ${headerText.substring(0, 50)}...)`);
      return fullVersion;
    }
    console.log("Failed to extract version from binary header:", headerText.substring(0, 50) + "...");
  } catch (e) {
    console.error("Error extracting version:", e);
    return null;
  }
  return null;
}

export function patchAtlasTextIfNeeded(text: string): string {
  const lines = text.split(/\r?\n/);
  if (lines.length < 4) return text;

  let index = 0;
  while (index < lines.length && !lines[index].trim()) index++;
  if (index >= lines.length) return text;

  let headerLastIndex = index;
  index++;

  for (; index < lines.length; index++) {
    const line = lines[index].trim();
    if (!line) {
      break;
    }
    if (line.startsWith("size:")) continue;
    if (line.startsWith("filter:")) continue;
    if (line.startsWith("repeat:")) continue;
    headerLastIndex = index - 1;
    break;
  }

  const afterHeaderIndex = headerLastIndex + 1;
  if (afterHeaderIndex < lines.length && lines[afterHeaderIndex].trim() === "") {
    return text;
  }

  const newLines = lines.slice();
  newLines.splice(afterHeaderIndex, 0, "");
  return newLines.join("\n");
}

export function parseAtlasPageNames(atlasText: string): string[] {
  const lines = atlasText.split(/\r?\n/);
  const result: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    if (line.includes(":")) continue;
    if (!(i === 0 || !lines[i - 1].trim())) continue;

    let next = "";
    for (let j = i + 1; j < lines.length; j++) {
      next = lines[j].trim();
      if (next) break;
    }
    if (!next) continue;
    if (next.startsWith("size:") || next.startsWith("filter:")) {
      result.push(line);
    }
  }
  return result;
}

export function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
